import { useRouter } from 'next/router'
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

interface NavigationHistoryContextValue {
  previousPage: string | null
  currentPage: string | null
  urlHistory: string[]
}

const NavigationHistoryContext = createContext<
  NavigationHistoryContextValue | undefined
>(undefined)

interface NavigationHistoryProviderProps {
  children: ReactNode
}

export const NavigationHistoryProvider = ({
  children
}: NavigationHistoryProviderProps) => {
  const router = useRouter()
  const [previousPage, setPreviousPage] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<string | null>(null)
  const [urlHistory, setUrlHistory] = useState<string[]>([])
  const isFirstRouteRef = useRef(true)

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (isFirstRouteRef.current) {
        isFirstRouteRef.current = false
        setPreviousPage(null)
      } else {
        setPreviousPage(currentPage)
      }
      setCurrentPage(url)
      setUrlHistory((prev) => [...prev, url])
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router, currentPage])

  useEffect(() => {
    if (typeof window !== 'undefined' && currentPage === null) {
      const initialUrl = window.location.pathname + window.location.search
      setCurrentPage(initialUrl)
      setUrlHistory([initialUrl])
    }
  }, [])

  const value = {
    previousPage,
    currentPage,
    urlHistory
  }

  return (
    <NavigationHistoryContext.Provider value={value}>
      {children}
    </NavigationHistoryContext.Provider>
  )
}

export const usePreviousPage = () => {
  const context = useContext(NavigationHistoryContext)
  if (!context) {
    throw new Error(
      'usePreviousPage must be used within NavigationHistoryProvider'
    )
  }
  if (typeof window === 'undefined') return null
  return context.previousPage || document.referrer || null
}

export const useNavigationHistory = () => {
  const context = useContext(NavigationHistoryContext)
  if (!context) {
    throw new Error(
      'useNavigationHistory must be used within NavigationHistoryProvider'
    )
  }
  return context
}

export const useUrlHistory = () => {
  const context = useContext(NavigationHistoryContext)
  if (!context) {
    throw new Error(
      'useUrlHistory must be used within NavigationHistoryProvider'
    )
  }
  return context.urlHistory
}
